import LookupApiService from '../api/lookupApiService';
import { ILibraryTagsRequest, INoTag } from '../api/models/tags';
import ITagsFetcher from '../typings/tags/tagsFetcher';
import { TagsSortOrder } from '../typings/tags/tagsSortOrder';

export interface ITagsFetcherParameters {
	api: LookupApiService;
	libraryId: number;
	sort: TagsSortOrder;
	filter: string;
	appliedTags?: string[];
	pageSize?: number;
	tagsGuid?: string;
	noTag?: INoTag;
	hideZeroCount?: boolean;
}

export default class TagsFetcher implements ITagsFetcher {
	public readonly filter: string;
	public readonly pageSize: number;
	private readonly appliedTags: string[];
	private readonly lookupApi: LookupApiService;
	private readonly libraryId: number;
	private readonly sort: TagsSortOrder;
	private readonly tagsGuid: string;
	private readonly noTag: INoTag;
	private readonly defaultPageSize = 50;
	private readonly hideZeroCount?: boolean;

	private nextPage = 0;
	private fetchedCount = 0;
	private hasNextPage = true;

	constructor(parameters: ITagsFetcherParameters) {
		this.filter = parameters.filter;
		this.appliedTags = parameters.appliedTags ?? [];
		this.pageSize = parameters.pageSize ?? this.defaultPageSize;
		this.libraryId = parameters.libraryId;
		this.sort = parameters.sort;
		this.lookupApi = parameters.api;
		this.tagsGuid = parameters.tagsGuid;
		this.noTag = parameters.noTag;
		this.hideZeroCount = parameters.hideZeroCount;
	}

	public async fetchNextPage() {
		if (!this.hasNextPage) {
			return null;
		}

		const data: ILibraryTagsRequest = {
			libraryId: this.libraryId,
			page: this.nextPage,
			pageSize: this.pageSize,
			filter: this.filter,
			requestGuid: this.tagsGuid ?? '00000000-0000-0000-0000-000000000000',
			appliedTags: this.appliedTags,
			tagsSort: this.sort,
			hideZeroCount: this.hideZeroCount
		};
		if (this.noTag && this.nextPage === 0) {
			data.noTagData = this.noTag;
		}

		const response = await this.lookupApi.fetchLibraryTagsAsync(data);
		this.fetchedCount += response.Tags.length;
		this.nextPage++;

		let remainingTagsCount = response.TotalCount - this.fetchedCount;

		// The API works differently in the case when we retrieve tags from the cache (using the guid) and from the database
		if (this.tagsGuid) {
			remainingTagsCount -= this.appliedTags.length;
		}

		this.hasNextPage = remainingTagsCount > 0;

		return {
			tags: response.Tags.map(t => ({ id: +t.Value, tag: t.Data, label: t.Text })),
			hasNextPage: this.hasNextPage
		};
	}
}
